import type { ApiStrapiProduct } from '../services/strapi/product/types';

type CategoryRoute = NonNullable<ApiStrapiProduct['category']>;

export function categoryIdentifier(category?: Partial<CategoryRoute> | null) {
  return category?.route || category?.slug || category?.documentId || 'equipment';
}
export function productIdentifier(product: Pick<ApiStrapiProduct, 'id' | 'slug' | 'documentId'>) {
  return product.slug || product.documentId || String(product.id);
}
export function categoryUrl(category?: Partial<CategoryRoute> | null) {
  return `/products/${encodeURIComponent(categoryIdentifier(category))}`;
}
export function productUrl(
  product: Pick<ApiStrapiProduct, 'id' | 'slug' | 'documentId' | 'category'>,
) {
  return `${categoryUrl(product.category)}/${encodeURIComponent(productIdentifier(product))}`;
}

export const formatPrice = (price: number | string) =>
  new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 }).format(Number(price) || 0) + ' ₽';
export function productPriceLabel(product: Pick<ApiStrapiProduct, 'price' | 'price_on_request'>) {
  if (Number(product.price) > 0) return formatPrice(product.price);
  return product.price_on_request ? 'Цена по запросу' : formatPrice(product.price);
}
export function productPriceUnit(
  product: Pick<ApiStrapiProduct, 'equipment_type' | 'key_features'>,
) {
  if (product.equipment_type !== 'cable') return '';
  const coil = product.key_features?.find((feature) => feature.label === 'Бухта')?.value;
  return coil ? `За бухту ${coil}` : '';
}
export function compareProductPrices(
  a: Pick<ApiStrapiProduct, 'price' | 'price_on_request'>,
  b: Pick<ApiStrapiProduct, 'price' | 'price_on_request'>,
  descending = false,
) {
  const unpriced = (product: typeof a) =>
    !Number.isFinite(Number(product.price)) || Number(product.price) <= 0;
  return (
    Number(unpriced(a)) - Number(unpriced(b)) ||
    (descending ? -1 : 1) * (Number(a.price) - Number(b.price))
  );
}
export function productStockLabel(
  product: Pick<ApiStrapiProduct, 'in_stock' | 'price_on_request'>,
) {
  return product.price_on_request
    ? 'Наличие уточняется'
    : product.in_stock
      ? 'В наличии'
      : 'Нет в наличии';
}
export function canPurchaseProduct(
  product: Pick<ApiStrapiProduct, 'in_stock' | 'price_on_request' | 'price'>,
) {
  return product.in_stock && !product.price_on_request && Number(product.price) > 0;
}
export function productCountLabel(count: number) {
  const category = new Intl.PluralRules('ru-RU').select(count);
  const forms: Record<string, string> = {
    one: 'товар',
    few: 'товара',
    many: 'товаров',
    other: 'товара',
  };
  return `${count} ${forms[category] || 'товаров'}`;
}
export function mediaUrl(path: string | undefined | null, base: string) {
  if (!path) return '';
  if (/^https?:\/\//i.test(path)) return path;
  if (path.startsWith('/')) return base.replace(/\/$/, '') + path;
  return '';
}
export function downloadText(filename: string, text: string) {
  const url = URL.createObjectURL(
    new Blob(['\uFEFF' + text], { type: 'text/plain;charset=utf-8' }),
  );
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.append(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 3000);
}
